import {
  FlatVehicleReport,
  FlatReportPropertyOrAddressInfo,
} from 'components/ChatBV/types';

import { normalizeArray, normalizeValue } from '../generalUtils';

export const mapVehicleReportToFlatSections = (
  reportData: any
): FlatVehicleReport => {
  const vehicle = reportData?.entities?.vehicles?.automobiles?.[0];
  const searchData = reportData?.meta?.search_data;

  return {
    permalink: normalizeValue(reportData?.meta?.permalink),

    // LOCATION: Geographic data from vehicle registration/ownership
    property_or_address_info: {
      city: normalizeValue(searchData?.city),
      state: normalizeValue(searchData?.state),
      zip5: normalizeValue(searchData?.zip5),
      country: normalizeValue(vehicle?.origin?.country),
      timezone: normalizeValue(vehicle?.origin?.timezone),
      latitude: normalizeValue(vehicle?.origin?.latitude),
      longitude: normalizeValue(vehicle?.origin?.longitude),
      precision: normalizeValue(vehicle?.origin?.precision),
      observe_dst: false,
    } as FlatReportPropertyOrAddressInfo | undefined,

    // VEHICLES: Vehicle information following FlatReportVehicles structure
    vehicles_info: {
      vehicle_details: {
        vin: normalizeValue(vehicle?.vin || searchData?.vin),
        vin_prefix: normalizeValue(vehicle?.vin_prefix),
        make: normalizeValue(vehicle?.make || searchData?.make),
        model: normalizeValue(vehicle?.model || searchData?.model),
        year: normalizeValue(vehicle?.year || searchData?.year),
        body_style: normalizeValue(vehicle?.body_style),
        type: normalizeValue(vehicle?.type),
        trim: normalizeValue(
          vehicle?.design?.trim || vehicle?.trim || searchData?.trim
        ),
        class: normalizeValue(vehicle?.class),
        current_color: normalizeValue(vehicle?.design?.current_color?.name),
        colors: normalizeArray(
          vehicle?.design?.colors?.map((c: any) => ({
            name: c.name,
            generic_name: c.generic_name,
            code: c.code,
          })) || []
        ),
        style: normalizeValue(vehicle?.design?.style),
        height: normalizeValue(vehicle?.design?.height),
        length: normalizeValue(vehicle?.design?.length),
        width: normalizeValue(vehicle?.design?.width),
        doors: normalizeValue(vehicle?.design?.doors),
        standard_seating: normalizeValue(vehicle?.design?.standard_seating),
        is_family_owned: normalizeValue(vehicle?.is_family_owned),
      },

      // MILEAGE: Current mileage
      mileage: vehicle?.mileage
        ? {
            miles: normalizeValue(vehicle.mileage.miles),
            date: normalizeValue(vehicle.mileage.date?.full),
          }
        : undefined,

      // ESTIMATED_MILEAGE: Estimated current mileage
      estimated_mileage: vehicle?.estimated_mileage
        ? {
            miles: normalizeValue(vehicle.estimated_mileage.miles),
            date: normalizeValue(vehicle.estimated_mileage.date?.full),
          }
        : undefined,

      // PRICING: MSRP, invoice price, etc.
      pricing: {
        msrp: normalizeValue(vehicle?.prices?.msrp),
        invoice_price: normalizeValue(vehicle?.prices?.invoice_price),
        delivery_charges: normalizeValue(vehicle?.prices?.delivery_charges),
        highest: normalizeValue(vehicle?.prices?.highest),
        average: normalizeValue(vehicle?.prices?.average),
        lowest: normalizeValue(vehicle?.prices?.lowest),
      },

      // VEHICLE_VALUATION: Market value, trade-in values
      valuation: vehicle?.valuation
        ? {
            market_value: normalizeValue(searchData?.market_value),
            clean_trade: normalizeValue(
              vehicle.valuation.clean_trade?.adjusted
            ),
            average_trade: normalizeValue(
              vehicle.valuation.average_trade?.adjusted
            ),
            rough_trade: normalizeValue(
              vehicle.valuation.rough_trade?.adjusted
            ),
            clean_retail: normalizeValue(
              vehicle.valuation.clean_retail?.adjusted
            ),
            loan_value: normalizeValue(vehicle.valuation.loan?.adjusted),
            region: normalizeValue(vehicle.valuation.region?.name),
          }
        : undefined,

      // SPECIFICATIONS: Engine, transmission, fuel economy
      specifications: vehicle?.specs
        ? {
            engine: vehicle.specs.engine
              ? {
                  engine: normalizeValue(vehicle.specs.engine.engine),
                  block: normalizeValue(vehicle.specs.engine.block),
                  cylinders: normalizeValue(vehicle.specs.engine.cylinders),
                  size: normalizeValue(vehicle.specs.engine.size),
                  fuel_type: normalizeValue(vehicle.specs.engine.fuel_type),
                  fuel_capacity: normalizeValue(
                    vehicle.specs.engine.fuel_capacity
                  ),
                }
              : undefined,
            transmission: vehicle.specs.transmission
              ? {
                  type: normalizeValue(vehicle.specs.transmission.type),
                  gears: normalizeValue(vehicle.specs.transmission.gears),
                }
              : undefined,
            drivetrain: normalizeValue(vehicle.specs.drivetrain),
            driven_wheels: normalizeValue(vehicle.specs.driven_wheels),
            city_mileage: normalizeValue(vehicle.specs.city_mileage),
            highway_mileage: normalizeValue(vehicle.specs.highway_mileage),
            curb_weight: normalizeValue(vehicle.specs.curb_weight),
            gross_weight: normalizeValue(vehicle.specs.gross_weight),
          }
        : undefined,

      // OWNERSHIP_HISTORY: Current and past owners, plus complete ownership history records
      ownership_history: (() => {
        const currentOwners = normalizeArray(
          vehicle?.owners?.map((o: any) => ({
            name: normalizeValue(o.name || o.full_name),
            location: normalizeValue(o.location || o.address),
            first_seen_date: normalizeValue(o.first_seen_date),
            last_seen_date: normalizeValue(o.last_seen_date),
          })) || []
        );
        const pastOwners = normalizeArray(
          vehicle?.past_owners?.map((o: any) => ({
            name: normalizeValue(o.name || o.full_name),
            location: normalizeValue(o.location || o.address),
            first_seen_date: normalizeValue(o.first_seen_date),
            last_seen_date: normalizeValue(o.last_seen_date),
          })) || []
        );
        const historyRecordsArray =
          vehicle?.ownership_history?.map((record: any) => ({
            data_date: normalizeValue(
              typeof record.data_date === 'string'
                ? record.data_date
                : record.data_date?.full
            ),
            dealer_type: normalizeValue(record.dealer_type),
            fleet: normalizeValue(record.fleet),
            is_new: normalizeValue(record.is_new),
            lease: normalizeValue(record.lease),
            lien_holder: normalizeValue(record.lien_holder),
            odometer: normalizeValue(record.odometer),
            owner_address: record.owner_address
              ? {
                  full: normalizeValue(record.owner_address.full),
                  city: normalizeValue(record.owner_address.parsed?.city),
                  state: normalizeValue(record.owner_address.parsed?.state),
                  zip5: normalizeValue(record.owner_address.parsed?.zip5),
                }
              : undefined,
            price: normalizeValue(record.price),
            pump: normalizeValue(record.pump),
            reg_class: normalizeValue(record.reg_class),
            segment: normalizeValue(record.segment),
            seller: normalizeValue(record.seller),
            seller_address: record.seller_address
              ? normalizeValue(
                  typeof record.seller_address === 'string'
                    ? record.seller_address
                    : record.seller_address.full
                )
              : undefined,
            state_data: normalizeValue(record.state_data),
          })) || [];
        const historyRecords = normalizeArray(historyRecordsArray);

        // Return undefined only if all three are empty/undefined
        if (!currentOwners && !pastOwners && !historyRecords) {
          return undefined;
        }
        return {
          current_owners: currentOwners,
          past_owners: pastOwners,
          ownership_history_records: historyRecords,
        };
      })(),

      // SALES_HISTORY: Transaction history (as array for FlatReportVehicles)
      sales_history: (() => {
        const transactions = normalizeArray(
          vehicle?.transactions?.map((t: any) => ({
            purchase_type: normalizeValue(t.purchase_type),
            date: normalizeValue(t.date?.full),
            published_date: normalizeValue(t.published_date?.full),
            purchased_date: normalizeValue(t.purchased_date?.full),
          })) || []
        );
        return transactions || undefined;
      })(),

      // DAMAGE_HISTORY: Accidents, salvage, reported damage, repair quotes (as array for FlatReportVehicles)
      damage_history: (() => {
        // Consolidate accidents from both top-level and damage_history.accidents
        const topLevelAccidents = vehicle?.accidents || [];
        const damageHistoryAccidents = vehicle?.damage_history?.accidents || [];
        const allAccidents = [...topLevelAccidents, ...damageHistoryAccidents];

        const accidents = normalizeArray(
          allAccidents.map((accident: any) => ({
            date: normalizeValue(accident.date?.full),
            damage_severity: normalizeValue(accident.damage_severity),
            impact_area: normalizeValue(accident.impact_area),
            crash_location: normalizeValue(accident.crash_location),
            airbags_deployed: normalizeValue(accident.airbags_deployed),
          }))
        );

        const salvage = normalizeArray(
          vehicle?.damage_history?.salvage || vehicle?.salvage || []
        );
        const reported_damage = normalizeArray(
          vehicle?.damage_history?.reported_damage || []
        );
        const repair_quotes = normalizeArray(
          vehicle?.damage_history?.repair_quotes || []
        );

        // Return array format for FlatReportVehicles
        const damageHistoryArray = [
          ...(accidents || []),
          ...(salvage || []),
          ...(reported_damage || []),
          ...(repair_quotes || []),
        ];

        return damageHistoryArray.length > 0 ? damageHistoryArray : undefined;
      })(),

      // LEASE: Lease information
      lease: vehicle?.lease
        ? {
            is_active: normalizeValue(vehicle.lease.is_active) ?? false,
            expiration_date: normalizeValue(vehicle.lease.expiration_date),
          }
        : undefined,
    },

    // Additional vehicle-specific properties (not in FlatReportVehicles but needed for vehicle reports)
    vehicle_images: (() => {
      const images = normalizeArray(
        vehicle?.images?.map((img: any) => ({
          url: normalizeValue(img.url),
          thumb: normalizeValue(img.thumb),
        })) || []
      );
      if (!images) {
        return undefined;
      }
      return { images };
    })(),

    vehicle_equipment: (() => {
      const equipment = normalizeArray(
        vehicle?.equipments?.map((eq: any) => ({
          name: normalizeValue(eq.name),
          group: normalizeValue(eq.group),
          attribute: normalizeValue(eq.attribute),
          value: normalizeValue(eq.value),
        })) || []
      );
      if (!equipment) {
        return undefined;
      }
      return { equipment };
    })(),

    warranties: (() => {
      const warranties = normalizeArray(
        vehicle?.warranties?.map((w: any) => {
          // Parse mileage_limit from "50000 miles" format
          const mileageLimit = w.miles
            ? parseInt(w.miles.toString().replace(/\D/g, '')) || undefined
            : undefined;

          return {
            type: normalizeValue(w.type),
            expiration_date: normalizeValue(w.months), // Store months as expiration_date
            mileage_limit: mileageLimit,
            is_active: normalizeValue(w.is_active) ?? false,
          };
        }) || []
      );
      if (!warranties) {
        return undefined;
      }
      return { warranties };
    })(),

    vehicle_origin: vehicle?.origin
      ? {
          country: normalizeValue(vehicle.origin.country),
          city: normalizeValue(vehicle.origin.city),
        }
      : undefined,

    recalls: (() => {
      const recalls = normalizeArray(
        vehicle?.recalls?.map((r: any) => ({
          campaign: normalizeValue(r.campaign),
          components: normalizeValue(r.components),
          consequence: normalizeValue(r.consequence),
          date: normalizeValue(
            typeof r.date === 'string' ? r.date : r.date?.full
          ),
          remedy: normalizeValue(r.remedy),
          summary: normalizeValue(r.summary),
          notes: normalizeValue(r.notes),
          oem_description: normalizeValue(r.oem_description),
          oem_recall_number: normalizeValue(r.oem_recall_number),
          status: normalizeValue(r.status),
          type: normalizeValue(r.type),
        })) || []
      );
      if (!recalls) {
        return undefined;
      }
      return { recalls };
    })(),

    safety_ratings: (() => {
      const safetyRatings = normalizeArray(
        vehicle?.safety_ratings?.map((sr: any) => ({
          vehicle_id: normalizeValue(sr.vehicle_id),
          vehicle_description: normalizeValue(sr.vehicle_description),
          vehicle_picture: normalizeValue(sr.vehicle_picture),
          overall_rating: normalizeValue(sr.overall_rating),
          complaints_count: normalizeValue(sr.complaints_count),
          investigation_count: normalizeValue(sr.investigation_count),
          recalls_count: normalizeValue(sr.recalls_count),
          nhtsa_electronic_stability_control: normalizeValue(
            sr.nhtsa_electronic_stability_control
          ),
          nhtsa_forward_collision_warning: normalizeValue(
            sr.nhtsa_forward_collision_warning
          ),
          nhtsa_lane_departure_warning: normalizeValue(
            sr.nhtsa_lane_departure_warning
          ),
          front_crash: normalizeValue(
            typeof sr.front_crash === 'string'
              ? sr.front_crash
              : sr.front_crash?.overall_rating !== undefined
              ? String(sr.front_crash.overall_rating || '')
              : sr.front_crash?.driver_side_rating !== undefined
              ? `${sr.front_crash.driver_side_rating}/${sr.front_crash.passenger_side_rating}`
              : undefined
          ),
          side_crash: normalizeValue(
            typeof sr.side_crash === 'string'
              ? sr.side_crash
              : sr.side_crash?.overall_rating !== undefined
              ? String(sr.side_crash.overall_rating || '')
              : sr.side_crash?.driver_side_rating !== undefined
              ? `${sr.side_crash.driver_side_rating}/${sr.side_crash.passenger_side_rating}`
              : undefined
          ),
          side_pole_crash: normalizeValue(
            typeof sr.side_pole_crash === 'string'
              ? sr.side_pole_crash
              : sr.side_pole_crash?.overall_rating !== undefined
              ? String(sr.side_pole_crash.overall_rating || '')
              : undefined
          ),
          rollover: normalizeValue(
            typeof sr.rollover === 'string'
              ? sr.rollover
              : sr.rollover?.rating !== undefined
              ? String(sr.rollover.rating || '')
              : sr.rollover?.possibility !== undefined
              ? String(sr.rollover.possibility || '')
              : undefined
          ),
        })) || []
      );
      if (!safetyRatings) {
        return undefined;
      }
      return { safety_ratings: safetyRatings };
    })(),

    ownership_costs: vehicle?.ownership_costs
      ? {
          total: normalizeValue(vehicle.ownership_costs.total),
          costs: normalizeArray(
            vehicle.ownership_costs.costs?.map((c: any) => ({
              depreciation: normalizeValue(c.depreciation),
              insurance: normalizeValue(c.insurance),
              fuel: normalizeValue(c.fuel),
              maintenance: normalizeValue(c.maintenance),
              repairs: normalizeValue(c.repairs),
              fees: normalizeValue(c.fees),
              total: normalizeValue(c.total),
            })) || []
          ),
          mileage_start: normalizeValue(vehicle.ownership_costs.mileage_start),
          mileage_per_year: normalizeValue(
            vehicle.ownership_costs.mileage_per_year
          ),
        }
      : undefined,

    number_of_liens: normalizeValue(
      vehicle?.liens?.length ?? vehicle?.liensCount
    ),
    number_of_thefts: normalizeValue(
      vehicle?.thefts?.length ?? vehicle?.theftsCount
    ),
    title_brands: normalizeValue(
      vehicle?.title_checks?.length !== undefined
        ? vehicle.title_checks.length
        : vehicle?.titleChecksCount
    ),
  } as FlatVehicleReport;
};
